import type {
  ApiError,
  BarsResponse,
  MarketStatus,
  QuotesResponse,
  RangeKey,
  SearchResponse,
} from "@chartandgo/shared";

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/$/, "");

/** An error thrown by the API client, carrying the machine-readable code. */
export class MarketApiError extends Error {
  readonly code: string;
  constructor(code: string, message: string) {
    super(message);
    this.name = "MarketApiError";
    this.code = code;
  }
}

async function apiGet<T>(path: string, params?: Record<string, string>): Promise<T> {
  const url = new URL(
    `${API_BASE}/api${path}`,
    API_BASE ? undefined : window.location.origin,
  );
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      url.searchParams.set(key, value);
    }
  }

  let response: Response;
  try {
    response = await fetch(url.toString());
  } catch {
    throw new MarketApiError(
      "network_error",
      "Could not reach the ChartAndGo server. Is it running?",
    );
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as ApiError | null;
    throw new MarketApiError(
      body?.error.code ?? "internal_error",
      body?.error.message ?? `Request failed with status ${response.status}.`,
    );
  }

  return (await response.json()) as T;
}

export function searchSymbols(query: string, signal?: AbortSignal) {
  return apiGetWithSignal<SearchResponse>("/search", { q: query }, signal);
}

export function fetchBars(symbol: string, range: RangeKey, before?: number) {
  return apiGet<BarsResponse>("/bars", {
    symbol,
    range,
    ...(before ? { before: String(before) } : {}),
  });
}

export function fetchQuotes(symbols: string[]) {
  return apiGet<QuotesResponse>("/quotes", { symbols: symbols.join(",") });
}

export function fetchMarketStatus() {
  return apiGet<MarketStatus>("/market-status");
}

/** Variant of apiGet that supports request cancellation for typeahead search. */
async function apiGetWithSignal<T>(
  path: string,
  params: Record<string, string>,
  signal?: AbortSignal,
): Promise<T> {
  const url = new URL(
    `${API_BASE}/api${path}`,
    API_BASE ? undefined : window.location.origin,
  );
  for (const [key, value] of Object.entries(params)) {
    url.searchParams.set(key, value);
  }

  let response: Response;
  try {
    response = await fetch(url.toString(), { signal });
  } catch (err) {
    if ((err as Error).name === "AbortError") throw err;
    throw new MarketApiError(
      "network_error",
      "Could not reach the ChartAndGo server. Is it running?",
    );
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as ApiError | null;
    throw new MarketApiError(
      body?.error.code ?? "internal_error",
      body?.error.message ?? `Request failed with status ${response.status}.`,
    );
  }
  return (await response.json()) as T;
}
